/*
  Product page gallery extras: arrows over the main image, and a small image
  pop-up in place of Dawn's full-page lightbox.

  Arrows (snippets/product-media-gallery.liquid): stepping presses the
  thumbnail next to the active one, so Dawn's media-gallery does the actual
  switch -- active states, scrolling the viewer, the live region -- exactly
  as for a thumbnail click. Wraps from the last image to the first.

  Pop-up (snippets/product-image-popup.liquid): a capturing click listener
  catches the main image's zoom button before Dawn's modal-opener sees it,
  and opens a <dialog> on the clicked image instead. showModal() supplies the
  backdrop, focus trapping and Escape; this adds the arrows, arrow keys, the
  counter, and closing on a click outside the card.
*/
(() => {
  /* ------------------------------ Arrows ------------------------------ */

  const step = (gallery, direction) => {
    const thumbs = [...gallery.querySelectorAll('[id^="GalleryThumbnails"] [data-target]')];
    if (thumbs.length < 2) return;
    const active = gallery.querySelector('[id^="GalleryViewer"] [data-media-id].is-active');
    const current = active ? thumbs.findIndex((t) => t.dataset.target === active.dataset.mediaId) : 0;
    const next = (Math.max(current, 0) + direction + thumbs.length) % thumbs.length;
    thumbs[next].querySelector('button')?.click();
  };

  document.addEventListener('click', (event) => {
    const arrow = event.target.closest?.('[data-gallery-step]');
    if (!arrow) return;
    const gallery = arrow.closest('media-gallery');
    if (gallery) step(gallery, Number(arrow.dataset.galleryStep));
  });

  /* ------------------------------ Pop-up ------------------------------ */

  /*
    Warm the pop-up's images so opening it, and stepping through it, never
    waits on the network. They are rendered loading="lazy" so they stay out
    of the page's first load; once the page has loaded and gone idle they are
    switched to eager at low priority, which fetches them even while hidden.
    Pointing at, touching or focusing the gallery warms them straight away,
    for a shopper who clicks before the page has gone idle. Measured on a
    typical home connection, each photo otherwise took 0.2-0.8s to appear
    after every click.
  */
  const warm = (popup) => {
    if (!popup || popup.dataset.warmed) return;
    popup.dataset.warmed = 'true';
    popup.querySelectorAll('.image-popup__image').forEach((image) => {
      image.fetchPriority = 'low';
      image.decoding = 'async';
      image.loading = 'eager';
    });
  };

  const warmAll = () => document.querySelectorAll('[data-image-popup]').forEach(warm);

  const whenIdle = (callback) =>
    'requestIdleCallback' in window ? requestIdleCallback(callback, { timeout: 3000 }) : setTimeout(callback, 1500);

  if (document.readyState === 'complete') whenIdle(warmAll);
  else window.addEventListener('load', () => whenIdle(warmAll), { once: true });

  ['pointerenter', 'touchstart', 'focusin'].forEach((type) =>
    document.addEventListener(
      type,
      (event) => {
        if (event.target.closest?.('media-gallery')) warmAll();
      },
      { capture: true, passive: true }
    )
  );

  const show = (popup, index) => {
    const images = [...popup.querySelectorAll('.image-popup__image')];
    if (!images.length) return;
    const i = (index + images.length) % images.length;
    images.forEach((image, n) => (image.hidden = n !== i));
    popup.dataset.index = i;
    const counter = popup.querySelector('[data-image-popup-current]');
    if (counter) counter.textContent = i + 1;
  };

  const open = (popup, mediaId) => {
    const images = [...popup.querySelectorAll('.image-popup__image')];
    const index = Math.max(0, images.findIndex((image) => image.dataset.mediaId === String(mediaId)));
    warm(popup);
    // The photo being opened, and its neighbours, at full priority.
    [index - 1, index, index + 1].forEach((n) => {
      const image = images[(n + images.length) % images.length];
      if (image) image.fetchPriority = 'high';
    });
    show(popup, index);
    if (!popup.open) popup.showModal();
    document.body.classList.add('image-popup-open');
  };

  // Capture phase, so Dawn's own handler on the button never runs. Matched on
  // the whole image opener, not only Dawn's invisible zoom button laid over
  // it: once the pop-up has been closed the button no longer sits on top of
  // the photo, and a second click landed on the image itself and did nothing.
  // The media id still comes from the button. Images only; video and 3D
  // media keep Dawn's own behaviour.
  document.addEventListener(
    'click',
    (event) => {
      const opener = event.target.closest?.('.product__modal-opener--image');
      const toggle = opener?.querySelector('.product__media-toggle');
      if (!toggle) return;
      // The pop-up renders beside Dawn's modal, outside <product-info>, so
      // look on the whole page when it is not inside the product block (the
      // block is checked first in case a page ever carries two products).
      const info = toggle.closest('product-info');
      const popup = info?.querySelector('[data-image-popup]') || document.querySelector('[data-image-popup]');
      if (!popup) return;
      event.preventDefault();
      event.stopPropagation();
      open(popup, toggle.dataset.mediaId);
    },
    true
  );

  document.addEventListener('click', (event) => {
    const popup = event.target.closest?.('[data-image-popup]');
    if (!popup) return;
    const stepButton = event.target.closest('[data-image-popup-step]');
    if (stepButton) {
      show(popup, Number(popup.dataset.index || 0) + Number(stepButton.dataset.imagePopupStep));
      return;
    }
    // The dialog itself is only the target when the click lands on the
    // backdrop around the card.
    if (event.target.closest('[data-image-popup-close]') || event.target === popup) popup.close();
  });

  document.addEventListener('keydown', (event) => {
    const popup = document.querySelector('[data-image-popup][open]');
    if (!popup || popup.querySelectorAll('.image-popup__image').length < 2) return;
    if (event.key === 'ArrowRight') show(popup, Number(popup.dataset.index || 0) + 1);
    if (event.key === 'ArrowLeft') show(popup, Number(popup.dataset.index || 0) - 1);
  });

  document.addEventListener(
    'close',
    (event) => {
      if (event.target.matches?.('[data-image-popup]')) document.body.classList.remove('image-popup-open');
    },
    true
  );
})();
